import { Suspense, useEffect } from 'react';
import { ThemeProvider } from '@cerebral-inc/ui';
import routes from 'routes';
import { QueryClient, QueryClientProvider } from 'react-query';
import { BrowserRouter, Redirect, Route, Switch } from 'react-router-dom';
import { ApiProvider } from 'contexts/ApiContext';
import { StoreProvider } from 'contexts/StoreContext';
import { initLogging } from 'utils/logging';
import { MfeConfiguration } from 'components/shared/mfe-configuration';
import initRum from 'components/shared/ErrorBoundary/utils/datadog';
import { BasicSpinner } from 'components/shared/spinner';
import ErrorBoundary from 'components/shared/ErrorBoundary';
import AssessmentLayout from './components/shared/layouts/AssessmentLayout';
import AppLayout from './components/shared/layouts';

initLogging();
const queryClient = new QueryClient();

interface AppProps {
  accessToken: string;
  client: string;
  uid: string;
  jwtToken: string;
  patientId: number;
  apiServerUrl: string;
  prefix: string;
  mfeUrl: string;
}

const App = ({
  accessToken,
  client,
  jwtToken,
  uid,
  patientId,
  apiServerUrl,
  prefix,
  mfeUrl
}: AppProps) => {
  useEffect(() => {
    initRum();
  }, []);

  useEffect(() => {
    MfeConfiguration.url = mfeUrl;
  }, [mfeUrl]);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <QueryClientProvider client={queryClient}>
          <ApiProvider
            accessToken={accessToken}
            client={client}
            jwtToken={jwtToken}
            uid={uid}
            serverbaseUrl={apiServerUrl}
          >
            <StoreProvider patientId={patientId}>
              <ThemeProvider>
                <Suspense fallback={<BasicSpinner />}>
                  <Switch>
                    <Route
                      path="/:url*"
                      exact
                      strict
                      render={props => (
                        <Redirect to={`${prefix}${props.location.pathname}/`} />
                      )}
                    />
                    {routes.map(route => (
                      <Route
                        key={route.path}
                        path={`${prefix}${route.path}`}
                        render={() => (
                          <AppLayout
                            Layout={AssessmentLayout}
                            key={route.path}
                            isGradientBackground={route.isGradientBackground}
                          >
                            <route.component />
                          </AppLayout>
                        )}
                      />
                    ))}
                    <AppLayout Layout={AssessmentLayout}>
                      <Redirect to={`${prefix}/adhd-self-test/`} />
                    </AppLayout>
                  </Switch>
                </Suspense>
              </ThemeProvider>
            </StoreProvider>
          </ApiProvider>
        </QueryClientProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
};

export default App;
